import React, { useState } from 'react';
import { MacroShock } from '../types';
import { Button } from './ui/Button';
import { Input } from './ui/Forms';
import { Trash2, Edit2, Check, X, Power, AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';
import { futureDate } from '../utils/currency';
interface MacroShockListProps {
  shocks: MacroShock[];
  onDelete: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Omit<MacroShock, 'id'>>) => void;
  onToggleActive: (id: string) => void;
}
function ImpactBadge({
  label,
  value
}: {
  label: string;
  value: number;
}) {
  const isNegative = value < 0;
  const isPositive = value > 0;
  if (value === 0) return null;
  return <div className="flex items-center gap-1.5 text-xs" data-id="element-1150">
      <span className="text-slate-500" data-id="element-1151">{label}:</span>
      <span className={`inline-flex items-center font-medium ${isNegative ? 'text-rose-600' : isPositive ? 'text-emerald-600' : 'text-slate-600'}`} data-id="element-1152">
        {isNegative ? <TrendingDown className="w-3 h-3 mr-0.5" data-id="element-1153" /> : isPositive ? <TrendingUp className="w-3 h-3 mr-0.5" data-id="element-1154" /> : null}
        {value > 0 ? '+' : ''}
        {value}%
      </span>
    </div>;
}
function ShockRow({
  shock,
  onDelete,
  onUpdate,
  onToggleActive
}: {
  shock: MacroShock;
  onDelete: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Omit<MacroShock, 'id'>>) => void;
  onToggleActive: (id: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(shock.name);
  const [editYear, setEditYear] = useState(shock.year.toString());
  const [editLiquid, setEditLiquid] = useState(shock.liquidImpact.toString());
  const [editIlliquid, setEditIlliquid] = useState(shock.illiquidImpact.toString());
  const [editRE, setEditRE] = useState(shock.realEstateImpact.toString());
  const isActive = shock.isActive ?? true;
  const handleSave = () => {
    const yearVal = Number(editYear);
    if (editName.trim() && !isNaN(yearVal)) {
      onUpdate(shock.id, {
        name: editName.trim(),
        year: yearVal,
        liquidImpact: Number(editLiquid) || 0,
        illiquidImpact: Number(editIlliquid) || 0,
        realEstateImpact: Number(editRE) || 0
      });
      setIsEditing(false);
    }
  };
  const handleCancel = () => {
    setEditName(shock.name);
    setEditYear(shock.year.toString());
    setEditLiquid(shock.liquidImpact.toString());
    setEditIlliquid(shock.illiquidImpact.toString());
    setEditRE(shock.realEstateImpact.toString());
    setIsEditing(false);
  };
  if (isEditing) {
    return <tr className="flex flex-col md:table-row border-b border-slate-100 last:border-0 bg-slate-50/50 p-4 md:p-0" data-id="element-1155">
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4" data-id="element-1156">
          <Input className="h-8 text-sm" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Event name" autoFocus data-id="element-1157" />
        </td>
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4" data-id="element-1158">
          <div className="flex items-center gap-1" data-id="element-1159">
            <span className="text-xs text-slate-500" data-id="element-1160">Yr</span>
            <Input type="number" className="w-16 h-8 text-sm px-2" value={editYear} onChange={e => setEditYear(e.target.value)} data-id="element-1161" />
          </div>
        </td>
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4" data-id="element-1162">
          <div className="flex flex-wrap gap-2" data-id="element-1163">
            <div className="flex items-center gap-1" data-id="element-1164">
              <span className="text-[10px] text-slate-500 uppercase" data-id="element-1165">Liq</span>
              <Input type="number" step="0.1" className="w-16 h-8 text-sm px-2" value={editLiquid} onChange={e => setEditLiquid(e.target.value)} data-id="element-1166" />
            </div>
            <div className="flex items-center gap-1" data-id="element-1167">
              <span className="text-[10px] text-slate-500 uppercase" data-id="element-1168">
                Illiq
              </span>
              <Input type="number" step="0.1" className="w-16 h-8 text-sm px-2" value={editIlliquid} onChange={e => setEditIlliquid(e.target.value)} data-id="element-1169" />
            </div>
            <div className="flex items-center gap-1" data-id="element-1170">
              <span className="text-[10px] text-slate-500 uppercase" data-id="element-1171">RE</span>
              <Input type="number" step="0.1" className="w-16 h-8 text-sm px-2" value={editRE} onChange={e => setEditRE(e.target.value)} data-id="element-1172" />
            </div>
          </div>
        </td>
        <td className="block md:table-cell px-0 py-2 md:px-4 md:py-4 text-left md:text-center" data-id="element-1173">
          <div className="flex items-center justify-start md:justify-center gap-1" data-id="element-1174">
            <Button variant="ghost" size="sm" onClick={handleSave} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-2" data-id="element-1175">
              <Check className="w-4 h-4" data-id="element-1176" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancel} className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 px-2" data-id="element-1177">
              <X className="w-4 h-4" data-id="element-1178" />
            </Button>
          </div>
        </td>
      </tr>;
  }
  return <>
      {/* Mobile View */}
      <tr className={`md:hidden block border-b border-slate-100 p-4 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1179">
        <td className="block p-0" data-id="element-1180">
          <div className="flex flex-col gap-3" data-id="element-1181">
            <div className="flex justify-between items-start gap-4" data-id="element-1182">
              <div className="flex flex-col" data-id="element-1183">
                <div className="flex items-center gap-2" data-id="element-1184">
                  <div className="p-1.5 rounded-md bg-rose-100 text-rose-700 shrink-0" data-id="element-1185">
                    <AlertTriangle className="w-3.5 h-3.5" data-id="element-1186" />
                  </div>
                  <span className="font-medium text-slate-900" data-id="element-1187">
                    {shock.name}
                  </span>
                  {!isActive && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1188">
                      INACTIVE
                    </span>}
                </div>
                <div className="mt-2 ml-8 flex flex-col gap-1" data-id="element-1189">
                  <ImpactBadge label="Liquid" value={shock.liquidImpact} data-id="element-1190" />
                  <ImpactBadge label="Illiquid" value={shock.illiquidImpact} data-id="element-1191" />
                  <ImpactBadge label="Real Estate" value={shock.realEstateImpact} data-id="element-1192" />
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-slate-50" data-id="element-1193">
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 gap-1.5" data-id="element-1194">
                Yr {shock.year}
                <span className="text-slate-400 font-normal" data-id="element-1195">
                  ({futureDate(shock.year)})
                </span>
              </span>
              <div className="flex items-center gap-1" data-id="element-1196">
                <Button variant="ghost" size="sm" onClick={() => onToggleActive(shock.id)} className={`px-2 ${isActive ? 'text-slate-400' : 'text-amber-500'}`} data-id="element-1197">
                  <Power className="w-4 h-4" data-id="element-1198" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="px-2 text-slate-400" data-id="element-1199">
                  <Edit2 className="w-4 h-4" data-id="element-1200" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(shock.id)} className="px-2 text-slate-400" data-id="element-1201">
                  <Trash2 className="w-4 h-4" data-id="element-1202" />
                </Button>
              </div>
            </div>
          </div>
        </td>
      </tr>

      {/* Desktop View */}
      <tr className={`hidden md:table-row hover:bg-slate-50/50 transition-colors group border-b border-slate-100 last:border-0 ${!isActive ? 'opacity-50' : ''}`} data-id="element-1203">
        <td className="px-4 py-4 font-medium text-slate-900" data-id="element-1204">
          <div className="flex items-center gap-2" data-id="element-1205">
            <div className="p-1.5 rounded-md bg-rose-100 text-rose-700 shrink-0" data-id="element-1206">
              <AlertTriangle className="w-3.5 h-3.5" data-id="element-1207" />
            </div>
            <span data-id="element-1208">{shock.name}</span>
            {!isActive && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-500" data-id="element-1209">
                INACTIVE
              </span>}
          </div>
        </td>
        <td className="px-4 py-4 text-slate-600" data-id="element-1210">
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 gap-1.5" data-id="element-1211">
            Yr {shock.year}
            <span className="text-slate-400 font-normal" data-id="element-1212">
              ({futureDate(shock.year)})
            </span>
          </span>
        </td>
        <td className="px-4 py-4" data-id="element-1213">
          <div className="flex flex-col gap-1" data-id="element-1214">
            <ImpactBadge label="Liquid" value={shock.liquidImpact} data-id="element-1215" />
            <ImpactBadge label="Illiquid" value={shock.illiquidImpact} data-id="element-1216" />
            <ImpactBadge label="Real Estate" value={shock.realEstateImpact} data-id="element-1217" />
          </div>
        </td>
        <td className="px-4 py-4 text-center" data-id="element-1218">
          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1219">
            <Button variant="ghost" size="sm" onClick={() => onToggleActive(shock.id)} className={`px-2 ${isActive ? 'text-slate-400 hover:text-amber-600' : 'text-amber-500 hover:text-emerald-600 !opacity-100'}`} title={isActive ? 'Deactivate' : 'Reactivate'} data-id="element-1220">
              <Power className="w-4 h-4" data-id="element-1221" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)} className="text-slate-400 hover:text-indigo-600 px-2" title="Edit" data-id="element-1222">
              <Edit2 className="w-4 h-4" data-id="element-1223" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(shock.id)} className="text-slate-400 hover:text-red-600 px-2" title="Delete" data-id="element-1224">
              <Trash2 className="w-4 h-4" data-id="element-1225" />
            </Button>
          </div>
        </td>
      </tr>
    </>;
}
export function MacroShockList({
  shocks,
  onDelete,
  onUpdate,
  onToggleActive
}: MacroShockListProps) {
  return <div className="h-full flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1226">
      {shocks.length === 0 ? <div className="p-8 text-center text-slate-500 flex flex-col items-center justify-center h-full min-h-[200px]" data-id="element-1227">
          <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mb-3" data-id="element-1228">
            <AlertTriangle className="w-6 h-6 text-slate-400" data-id="element-1229" />
          </div>
          <p data-id="element-1230">No macro shocks added yet.</p>
          <p className="text-sm mt-1" data-id="element-1231">
            Add events to stress test your portfolio against market downturns.
          </p>
        </div> : <>
          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-slate-100" data-id="element-1232">
            {shocks.map(shock => <ShockRow key={shock.id} shock={shock} onDelete={onDelete} onUpdate={onUpdate} onToggleActive={onToggleActive} data-id="element-1233" />)}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto" data-id="element-1234">
            <table className="w-full text-sm text-left" data-id="element-1235">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1236">
                <tr data-id="element-1237">
                  <th className="px-4 py-3 font-medium" data-id="element-1238">Event</th>
                  <th className="px-4 py-3 font-medium" data-id="element-1239">Timing</th>
                  <th className="px-4 py-3 font-medium" data-id="element-1240">Impact</th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-1241">Actions</th>
                </tr>
              </thead>
              <tbody data-id="element-1242">
                {shocks.map(shock => <ShockRow key={shock.id} shock={shock} onDelete={onDelete} onUpdate={onUpdate} onToggleActive={onToggleActive} data-id="element-1243" />)}
              </tbody>
            </table>
          </div>
        </>}
    </div>;
}